import React, { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { useCheckListDefaultFilters } from './useCheckListDefaultFilters';

interface FilterSettings {
  status: (string | number)[];
  employeeId: (string | number)[];
  orderTypeId: (string | number)[];
  revenueCenterId: (string | number)[];
  orderChannelId: (string | number)[];
}

type FilterSettingsContextType = {
  filterSettings: FilterSettings;
  setFilterSettings: (settings: Partial<FilterSettings>) => void;
  resetFilterSettings: () => void;
}

const FilterSettingsContext = createContext<FilterSettingsContextType | undefined>(undefined);

// This provider keeps the check list filters in memory as long as the current user's sessions last.
// There are persisted in memory this means that they will be restored on refresh, 
// user logout, version update and other actions that refresh the pos within a terminal.
const FilterSettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const defaultFilterSettings = useCheckListDefaultFilters();
  const [settings, setSettings] = useState<FilterSettings>(defaultFilterSettings);

  // Default filters depend on terminal defaults, so we need to update them
  // when the terminal defaults are loaded.
  useEffect(() => {
    if (!defaultFilterSettings.isLoading) {
      setSettings(defaultFilterSettings);
    }
  }, [defaultFilterSettings]);

  const handleSetFilterSettings = useCallback((newSettings: Partial<FilterSettings>) => {
    setSettings((prevSettings) => ({ ...prevSettings, ...newSettings }));
  }, []);

  const handleResetFilterSettings = useCallback(() => {
    setSettings(defaultFilterSettings);
  }, [defaultFilterSettings]);

  const value = useMemo(() => ({
    filterSettings: settings,
    setFilterSettings: handleSetFilterSettings,
    resetFilterSettings: handleResetFilterSettings,
  }), [settings, handleSetFilterSettings, handleResetFilterSettings]);

  return (
    <FilterSettingsContext.Provider value={value}>
      {children}
    </FilterSettingsContext.Provider>
  );
};

export type { FilterSettings };
export { FilterSettingsContext, FilterSettingsProvider };
